import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { XMarkIcon, PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [images, setImages] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();

  // جلب بيانات المنتج
  const { data: product, isLoading: productLoading, error: productError } = useQuery(
    ['product', id],
    () => api.get(`/products/vendor/edit/${id}`).then(res => {
      console.log('Full API Response:', res);
      console.log('Response data:', res.data);
      // تجربة كلا الاحتمالين
      const productData = res.data.data || res.data;
      console.log('Product data extracted:', productData);
      return productData;
    }),
    {
      retry: false,
      onSuccess: (data) => {
        console.log('Product data in onSuccess:', data);
        if (!data) {
          console.error('Data is null or undefined');
          return;
        }
        
        // تعبئة النموذج
        setValue('name', data.name || '');
        setValue('description', data.description || '');
        setValue('category', data.category_id || '');
        setValue('price', data.price || 0);
        setValue('comparePrice', data.compare_price || '');
        setValue('inventory.sku', data.sku || '');
        setValue('inventory.quantity', data.quantity || 0);
        setValue('inventory.trackQuantity', data.track_quantity !== false);
        setValue('inventory.allowBackorder', data.allow_backorder || false);
        setValue('inventory.lowStockThreshold', data.low_stock_threshold || 5);
        setValue('shipping.freeShipping', data.shipping_free || false);
        setValue('shipping.shippingCost', data.shipping_cost || 0);
        setValue('shipping.processingTime', data.processing_time || '1-2 أيام عمل');
        setValue('status', data.status || 'draft');
        
        console.log('Form values set successfully');
        
        // تعيين الصور
        if (data.images && Array.isArray(data.images) && data.images.length > 0) {
          const imageUrls = data.images.map(img => {
            if (typeof img === 'string') return img;
            if (img && img.url) return img.url;
            return null;
          }).filter(Boolean);
          console.log('Image URLs:', imageUrls);
          setImagePreview(imageUrls);
        }
      },
      onError: (error) => {
        console.error('Error loading product:', error);
        console.error('Error response:', error.response);
        toast.error(error.response?.data?.message || 'المنتج غير موجود');
        setTimeout(() => navigate('/vendor/products'), 2000);
      }
    }
  );

  // جلب الفئات
  const { data: categories } = useQuery(
    'categories',
    () => api.get('/categories').then(res => res.data.data)
  );

  // تحديث منتج
  const updateProductMutation = useMutation(
    (productData) => api.put(`/products/${id}`, productData),
    {
      onSuccess: () => {
        toast.success('تم تحديث المنتج بنجاح!');
        queryClient.invalidateQueries('vendor-products');
        navigate('/vendor/products');
      },
      onError: (error) => {
        console.error('Update error:', error.response?.data);
        const message = error.response?.data?.message || 'حدث خطأ في تحديث المنتج';
        const errors = error.response?.data?.errors;
        
        if (errors) {
          Object.keys(errors).forEach(key => {
            toast.error(`${key}: ${errors[key][0]}`);
          });
        } else {
          toast.error(message);
        }
      }
    }
  );

  // معالجة رفع الصور
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length + imagePreview.length > 5) {
      toast.error('يمكنك رفع 5 صور كحد أقصى');
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  // حذف صورة
  const removeImage = (index) => {
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  // إرسال النموذج
  const onSubmit = async (data) => {
    if (imagePreview.length === 0) {
      toast.error('يجب إضافة صورة واحدة على الأقل');
      return;
    }

    // تحويل القيم بشكل آمن
    const quantity = data['inventory.quantity'];
    const parsedQuantity = quantity ? parseInt(quantity, 10) : 0;
    
    const lowStockThreshold = data['inventory.lowStockThreshold'];
    const parsedLowStock = lowStockThreshold ? parseInt(lowStockThreshold, 10) : 5;
    
    // التأكد من أن SKU نص
    const sku = data['inventory.sku'];
    const skuString = sku ? String(sku).trim() : '';

    // بناء البيانات بالتنسيق الصحيح
    const productData = {
      name: data.name,
      description: data.description,
      category: data.category,
      price: parseFloat(data.price),
      comparePrice: data.comparePrice ? parseFloat(data.comparePrice) : null,
      status: data.status,
      inventory: {
        sku: skuString,
        quantity: isNaN(parsedQuantity) ? 0 : parsedQuantity,
        trackQuantity: Boolean(data['inventory.trackQuantity']),
        allowBackorder: Boolean(data['inventory.allowBackorder']),
        lowStockThreshold: isNaN(parsedLowStock) ? 5 : parsedLowStock
      },
      shipping: {
        freeShipping: Boolean(data['shipping.freeShipping']),
        shippingCost: data['shipping.freeShipping'] ? 0 : parseFloat(data['shipping.shippingCost'] || 0),
        processingTime: String(data['shipping.processingTime'] || '1-2 أيام عمل')
      },
      images: imagePreview.map((preview, index) => ({
        url: preview,
        alt: data.name,
        isMain: index === 0
      }))
    };

    console.log('Submitting product data:', JSON.stringify(productData, null, 2));
    updateProductMutation.mutate(productData);
  };

  if (productLoading) {
    return (
      <VendorLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </VendorLayout>
    );
  }

  if (productError) {
    return (
      <VendorLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-900 mb-2">المنتج غير موجود</h3>
          <p className="text-red-700 mb-4">
            {productError.response?.data?.message || 'لم يتم العثور على المنتج المطلوب'}
          </p>
          <button
            onClick={() => navigate('/vendor/products')}
            className="btn-primary"
          >
            العودة للمنتجات
          </button>
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">تعديل المنتج</h1>
            <button
              type="button"
              onClick={() => navigate('/vendor/products')}
              className="text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* معلومات أساسية */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">اسم المنتج *</label>
                <input
                  type="text"
                  className="form-input"
                  {...register('name', { required: 'اسم المنتج مطلوب' })}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
              </div>

              <div>
                <label className="form-label">الفئة *</label>
                <select
                  className="form-input"
                  {...register('category', { required: 'الفئة مطلوبة' })}
                >
                  <option value="">اختر الفئة</option>
                  {categories?.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>}
              </div>
            </div>

            {/* الوصف */}
            <div>
              <label className="form-label">وصف المنتج *</label>
              <textarea
                rows={4}
                className="form-input"
                {...register('description', { required: 'وصف المنتج مطلوب' })}
              />
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>}
            </div>

            {/* الأسعار */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">السعر (جنيه) *</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  {...register('price', { required: 'السعر مطلوب' })}
                />
                {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price.message}</p>}
              </div>

              <div>
                <label className="form-label">سعر المقارنة</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  {...register('comparePrice')}
                />
              </div>
            </div>

            {/* المخزون */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">رمز المنتج (SKU)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="مثال: PROD-001 (اختياري)"
                  {...register('inventory.sku')}
                />
                {errors['inventory.sku'] && <p className="text-red-500 text-sm mt-1">{errors['inventory.sku'].message}</p>}
              </div>

              <div>
                <label className="form-label">الكمية *</label>
                <input
                  type="number"
                  className="form-input"
                  min="0"
                  step="1"
                  name='inventory.quantity'
                  {...register('inventory.quantity', { 
                    required: 'الكمية مطلوبة',
                    valueAsNumber: true,
                    min: { value: 0, message: 'الكمية يجب أن تكون 0 أو أكثر' }
                  })}
                />
                {errors['inventory.quantity'] && <p className="text-red-500 text-sm mt-1">{errors['inventory.quantity'].message}</p>}
              </div>
            </div>

            {/* الصور */}
            <div>
              <label className="form-label">صور المنتج *</label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                {imagePreview.map((preview, index) => (
                  <div key={index} className="relative">
                    <img
                      src={preview}
                      alt={`Preview ${index}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {imagePreview.length < 5 && (
                  <label className="border-2 border-dashed border-gray-300 rounded-lg h-32 flex items-center justify-center cursor-pointer hover:border-primary-500">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <PhotoIcon className="w-8 h-8 text-gray-400" />
                  </label>
                )}
              </div>
            </div>

            {/* إعدادات الشحن */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium mb-4">إعدادات الشحن</h3>
              
              <div className="space-y-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    {...register('shipping.freeShipping')}
                  />
                  <span className="mr-2 text-sm text-gray-700">شحن مجاني</span>
                </label>

                {!watch('shipping.freeShipping') && (
                  <div>
                    <label className="form-label">تكلفة الشحن (جنيه)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-input"
                      placeholder="50.00"
                      {...register('shipping.shippingCost', {
                        valueAsNumber: true
                      })}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      اتركه فارغاً أو 0 لاستخدام رسوم الشحن الافتراضية (50 جنيه)
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* الحالة */}
            <div>
              <label className="form-label">حالة المنتج</label>
              <select className="form-input" {...register('status')}>
                <option value="draft">مسودة</option>
                <option value="active">نشط</option>
              </select>
            </div>

            {/* أزرار */}
            <div className="flex justify-end space-x-4 space-x-reverse">
              <button
                type="button"
                onClick={() => navigate('/vendor/products')}
                className="btn-secondary"
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={updateProductMutation.isLoading}
                className="btn-primary"
              >
                {updateProductMutation.isLoading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </VendorLayout>
  );
};

export default EditProduct;
